/* Global Embla initializer for the theme
   - Exposes window.PV = window.PV || {} and window.PV.initEmbla(el, config)
   - Handles late-loaded Embla (retry/backoff), plugin detection, autoplay plugin preference,
     accessible dots creation and instance exposure for debugging.
   - Supports both standard Embla classes (.embla__*) and custom theme classes (.hero-banner__*)
   - Keep this file generic so all blocks using Embla can call it with minimal config.
*/
(function () {
  if (typeof window === 'undefined') return;
  window.PV = window.PV || {};

  // Helper to find element by multiple selectors
  function findElement(root, selectors) {
    const selectorArray = selectors.split(',').map(s => s.trim());
    for (const selector of selectorArray) {
      const element = root.querySelector(selector);
      if (element) return element;
    }
    return null;
  }

  // Helper to find elements by multiple selectors
  function findElements(root, selectors) {
    const selectorArray = selectors.split(',').map(s => s.trim());
    for (const selector of selectorArray) {
      const elements = root.querySelectorAll(selector);
      if (elements.length > 0) return elements;
    }
    return [];
  }

  function detectPluginConstructors() {
    const lookups = {
      autoplay: (window.EmblaAutoplay || window.EmblaCarouselAutoplay || window.EmblaAutoplayPlugin),
      autoScroll: (window.EmblaAutoScroll || window.EmblaCarouselAutoScroll),
      autoHeight: (window.EmblaAutoHeight || window.EmblaCarouselAutoHeight),
      classNames: (window.EmblaClassNames || window.EmblaCarouselClassNames),
      fade: (window.EmblaFade || window.EmblaCarouselFade),
      wheelGestures: (window.EmblaWheelGestures || window.EmblaCarouselWheelGestures),
    };
    return lookups;
  }

  // small helper to create accessible dot buttons
  function buildDots(container, embla, opts) {
    function createDots() {
      const slides = embla.slideNodes();
      container.innerHTML = '';
      
      // Generate dots based on scroll snap list like official example
      const snapList = embla.scrollSnapList();
      const fragment = document.createDocumentFragment();
      
      snapList.forEach((snap, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'embla__dot';
        btn.setAttribute('aria-label', `Go to slide ${i + 1}`);
        btn.setAttribute('role', 'tab');
        btn.setAttribute('data-index', String(i));
        btn.setAttribute('aria-current', 'false');
        btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
        btn.addEventListener('click', () => embla.scrollTo(i));
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'Right') embla.scrollNext();
          if (e.key === 'ArrowLeft' || e.key === 'Left') embla.scrollPrev();
          if (e.key === 'Home') embla.scrollTo(0);
          if (e.key === 'End') embla.scrollTo(snapList.length - 1);
        });
        fragment.appendChild(btn);
      });
      container.appendChild(fragment);
    }

    function sync() {
      const selected = embla.selectedScrollSnap();
      const dots = Array.from(container.children);
      dots.forEach((d, idx) => {
        d.setAttribute('aria-current', idx === selected ? 'true' : 'false');
        d.setAttribute('tabindex', idx === selected ? '0' : '-1');
        if (idx === selected) {
          d.classList.add('embla__dot--selected');
        } else {
          d.classList.remove('embla__dot--selected');
        }
      });
    }

    // Initial creation
    createDots();

    embla.on('select', sync);
    embla.on('init', sync);
    embla.on('reInit', createDots); // Rebuild dots on reInit
    embla.on('reInit', sync); // Then sync active state
    // ensure initial state
    sync();
  }

  // Retry/backoff if EmblaCarousel is not present yet
  function waitForEmbla(maxAttempts, intervalBase) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      function tryOnce() {
        attempts += 1;
        if (typeof window.EmblaCarousel === 'function') return resolve(window.EmblaCarousel);
        if (attempts >= maxAttempts) return reject(new Error('EmblaCarousel not available'));
        setTimeout(tryOnce, intervalBase * attempts);
      }
      tryOnce();
    });
  }

  // The exported initializer
  window.PV.initEmbla = function initEmbla(rootEl, cfg = {}) {
    if (!rootEl) return Promise.reject(new Error('Missing root element'));
    if (rootEl.__pv_embla_init) return Promise.resolve(rootEl.__pv_embla_instance);
    rootEl.__pv_embla_init = true;

    const defaults = {
      viewportSelector: '.hero-banner__viewport',
      containerSelector: '.hero-banner__container',
      slideSelector: '.hero-banner__slide',
      prevSelector: '.hero-banner__prev',
      nextSelector: '.hero-banner__next',
      dotsSelector: '.hero-banner__dots',
      showDots: true,
      showArrows: true,
      autoplay: false,
      autoplayDelay: 5000,
      emblaOptions: { loop: true, align: 'start', containScroll: 'trimSnaps', dragFree: false, duration: 25 },
      maxEmblaWaitAttempts: 6,
      emblaWaitIntervalBase: 200,
    };

    const options = Object.assign({}, defaults, cfg);

    return waitForEmbla(options.maxEmblaWaitAttempts, options.emblaWaitIntervalBase)
      .then((Embla) => {
        const pluginsLookup = detectPluginConstructors();
        const plugins = [];

        // prefer official Autoplay plugin when available and requested
        if (options.autoplay && pluginsLookup.autoplay) {
          try {
            plugins.push(pluginsLookup.autoplay({ delay: options.autoplayDelay || 5000, stopOnInteraction: true }));
          } catch (e) {
            // plugin constructor signature may differ; fall back to manual later
          }
        }

        // Add other plugins if constructors found and config asks for them
        if (options.useClassNames && pluginsLookup.classNames) {
          plugins.push(pluginsLookup.classNames());
        }
        if (options.useAutoHeight && pluginsLookup.autoHeight) {
          plugins.push(pluginsLookup.autoHeight());
        }
        if (options.useAutoScroll && pluginsLookup.autoScroll) {
          plugins.push(pluginsLookup.autoScroll());
        }

        const viewport = findElement(rootEl, options.viewportSelector);
        if (!viewport) throw new Error('Embla viewport not found for: ' + options.viewportSelector);

        const embla = Embla(viewport, options.emblaOptions, plugins.length ? plugins : undefined);

        // store instance for debug and future access
        rootEl.__pv_embla_instance = embla;
        window.__PV_EMBLAS = window.__PV_EMBLAS || [];
        window.__PV_EMBLAS.push(embla);

        // arrows
        if (options.showArrows) {
          const prev = findElement(rootEl, options.prevSelector);
          const next = findElement(rootEl, options.nextSelector);
          if (prev) prev.addEventListener('click', () => embla.scrollPrev());
          if (next) next.addEventListener('click', () => embla.scrollNext());
          // Keyboard control for arrows
          [prev, next].forEach((btn, i) => {
            if (!btn) return;
            btn.setAttribute('tabindex', '0');
            btn.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                i === 0 ? embla.scrollPrev() : embla.scrollNext();
              }
              if (e.key === 'Home') embla.scrollTo(0);
              if (e.key === 'End') embla.scrollTo(embla.slideNodes().length - 1);
            });
          });
        }

        // dots
        if (options.showDots) {
          const dotsContainer = findElement(rootEl, options.dotsSelector);
          if (dotsContainer) {
            dotsContainer.setAttribute('role', 'tablist');
            buildDots(dotsContainer, embla, options);
          }
        }

        // selection state: apply notch class to current slide; mirror for RTL
        function syncActiveSlide() {
          const slides = Array.from(findElements(rootEl, options.slideSelector));
          const selected = embla.selectedScrollSnap();
          slides.forEach((s, idx) => {
            if (idx === selected) s.classList.add('is-active'); else s.classList.remove('is-active');
          });
          // RTL support: add dir-rtl class on root for CSS to mirror notch
          const dir = document.documentElement.getAttribute('dir') || document.body.getAttribute('dir') || 'ltr';
          if (dir.toLowerCase() === 'rtl') rootEl.classList.add('dir-rtl'); else rootEl.classList.remove('dir-rtl');
        }
        embla.on('init', syncActiveSlide);
        embla.on('reInit', syncActiveSlide);
        embla.on('select', syncActiveSlide);

        // fallback manual autoplay if no plugin was used
        let manualAutoplayTimer = null;
        let autoplayPermanentlyStopped = false;

        function startManualAutoplay() {
          if (!options.autoplay) return;
          if (autoplayPermanentlyStopped) return;
          if (manualAutoplayTimer) return;
          manualAutoplayTimer = setInterval(() => {
            if (document.hidden) return;
            if (autoplayPermanentlyStopped) {
              stopManualAutoplay();
              return;
            }
            embla.scrollNext();
          }, options.autoplayDelay || 5000);
        }
        function stopManualAutoplay() {
          if (!manualAutoplayTimer) return;
          clearInterval(manualAutoplayTimer);
          manualAutoplayTimer = null;
        }
        function permanentlyStopAutoplay() {
          autoplayPermanentlyStopped = true;
          stopManualAutoplay();
        }
        function resumeAutoplayHandler() {
          autoplayPermanentlyStopped = false;
          startManualAutoplay();
        }

        // Listen for custom stop/resume events (e.g., from category filter or video play)
        rootEl.addEventListener('pv:stopAutoplay', permanentlyStopAutoplay);
        rootEl.addEventListener('pv:resumeAutoplay', resumeAutoplayHandler);

        // If we didn't push an autoplay plugin but autoplay is requested, start manual autoplay
        if (options.autoplay && (!pluginsLookup.autoplay || plugins.length === 0)) {
          startManualAutoplay();
          // pause on interaction
          rootEl.addEventListener('mouseenter', stopManualAutoplay);
          rootEl.addEventListener('mouseleave', () => { if (!autoplayPermanentlyStopped) startManualAutoplay(); });
          rootEl.addEventListener('focusin', stopManualAutoplay);
          rootEl.addEventListener('focusout', () => { if (!autoplayPermanentlyStopped) startManualAutoplay(); });
          rootEl.addEventListener('pointerdown', stopManualAutoplay);
          rootEl.addEventListener('touchstart', stopManualAutoplay, { passive: true });
          document.addEventListener('visibilitychange', () => {
            if (document.hidden) stopManualAutoplay(); 
            else if (!autoplayPermanentlyStopped) startManualAutoplay();
          });
        }

        return embla;
      })
      .catch((err) => {
        // console.warn('Embla initialization failed for element', rootEl, err);
        return null;
      });
  };
})();
